import { z } from 'zod';
import type { SettingDataType } from '../types/setting';

/**
 * Dynamic schema factory — generates a zod schema appropriate for the
 * given data type and constraints. This is used as the react-hook-form
 * resolver for SystemSettingEditDialog.
 *
 * We use z.string().superRefine() to avoid chained .refine() type
 * mismatches that TypeScript cannot resolve when the chain length is
 * conditional.
 */
export function buildSettingValueSchema(
  dataType: SettingDataType,
  options?: {
    minValue?: number | null;
    maxValue?: number | null;
    enumValues?: string | null;
  }
) {
  switch (dataType) {
    case 'BOOLEAN':
      return z.object({
        value: z.enum(['true', 'false'], {
          errorMap: () => ({ message: "'true' 또는 'false' 만 입력 가능합니다" }),
        }),
      });

    case 'INTEGER': {
      const min = options?.minValue ?? null;
      const max = options?.maxValue ?? null;
      return z.object({
        value: z.string().superRefine((v, ctx) => {
          if (!v || v.trim() === '') {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: '값을 입력해주세요' });
            return;
          }
          if (!/^-?\d+$/.test(v)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: '정수 형식이 아닙니다' });
            return;
          }
          const num = parseInt(v, 10);
          if (min !== null && num < min) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `최솟값은 ${min} 입니다` });
          }
          if (max !== null && num > max) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `최댓값은 ${max} 입니다` });
          }
        }),
      });
    }

    case 'STRING':
      return z.object({
        value: z.string().superRefine((v, ctx) => {
          if (!v || v.trim() === '') {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: '값을 입력해주세요' });
            return;
          }
          if (v.length > 512) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: '최대 512자까지 입력 가능합니다' });
          }
          if (/<[^>]*script/i.test(v) || /<[^>]+>/i.test(v)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'HTML/스크립트 태그는 입력할 수 없습니다',
            });
          }
        }),
      });

    case 'ENUM': {
      const allowed = (options?.enumValues ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (allowed.length === 0) {
        return z.object({ value: z.string().min(1, '값을 선택해주세요') });
      }
      return z.object({
        value: z.enum(allowed as [string, ...string[]], {
          errorMap: () => ({
            message: `다음 중 하나를 선택해주세요: ${allowed.join(', ')}`,
          }),
        }),
      });
    }

    default:
      return z.object({ value: z.string().min(1, '값을 입력해주세요') });
  }
}

export type SettingEditFormValues = { value: string };
